import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus, HeartPulse, Star } from "lucide-react";
import client from "../lib/api";
import { MOOD_BY_KEY, MOODS } from "./MoodPicker";

// A short label for how far back a week sits from now.
function weekLabel(w, idx, total) {
  if (w.is_current) return "this wk";
  const back = total - 1 - idx;
  if (back === 1) return "last wk";
  return `${back}w ago`;
}

/**
 * A quiet week by week strip of how the person's notes have felt. Reads
 * from /engagement/mood-trend, which folds each note's mood to a small
 * score and averages it per week. Renders nothing until at least one
 * mood has been logged, so a blank journal stays calm. Never a score.
 */
export default function MoodTrend({ weeks = 6, refreshKey = 0, testid = "mood-trend" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    client.get(`/engagement/mood-trend?weeks=${weeks}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [weeks, refreshKey]);

  if (!data || !data.has_data) return null;

  const trend = data.trend;
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const trendColor = trend === "up" ? "178 82% 34%" : trend === "down" ? "28 92% 54%" : "230 15% 50%";
  const trendWord = trend === "up" ? "lifting" : trend === "down" ? "dipping" : "steady";
  const total = data.weeks.length;

  return (
    <div className="k-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="k-label flex items-center gap-2 !mb-0"><HeartPulse size={13} /> mood, week by week</div>
        {trend && (
          <span
            className="k-chip !text-xs flex items-center gap-1"
            style={{ color: `hsl(${trendColor})`, background: `hsl(${trendColor} / 0.1)`, border: "none" }}
            data-testid="mood-trend-pill"
          >
            <TrendIcon size={12} /> {trendWord}
          </span>
        )}
      </div>

      <div className="flex items-end justify-between gap-2" data-testid="mood-trend-strip">
        {data.weeks.map((w, idx) => {
          const meta = w.dominant_mood ? MOOD_BY_KEY[w.dominant_mood] : null;
          const pct = w.avg_score ? (w.avg_score / 5) * 100 : 0;
          return (
            <div
              key={w.week_start}
              className="flex-1 flex flex-col items-center gap-1.5"
              title={`${w.logged} note${w.logged === 1 ? "" : "s"}${meta ? ", leaning " + meta.label : ""}${w.full_week ? " (a full week)" : ""}`}
              data-testid={`mood-week-${idx}`}
            >
              <div className="h-4 flex items-center justify-center">
                {w.full_week && (
                  <Star size={12} style={{ color: "hsl(38 92% 48%)", fill: "hsl(38 92% 48%)" }} data-testid={`mood-week-${idx}-full`} />
                )}
              </div>
              <div
                className="w-full relative rounded-lg overflow-hidden flex items-end"
                style={{ height: 72, background: "hsl(var(--klareet-canvas-2))" }}
              >
                {meta ? (
                  <div
                    className="w-full rounded-lg transition-all duration-500"
                    style={{ height: `${Math.max(14, pct)}%`, background: meta.color }}
                  />
                ) : (
                  <div className="w-full flex items-end justify-center pb-1.5">
                    <span className="w-1 h-1 rounded-full" style={{ background: "hsl(var(--klareet-line))" }} />
                  </div>
                )}
              </div>
              <span
                className="k-quiet !text-[10px]"
                style={{ fontWeight: w.is_current ? 600 : 400, color: w.is_current ? "hsl(var(--klareet-ink))" : undefined }}
              >
                {weekLabel(w, idx, total)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-4 flex-wrap" data-testid="mood-trend-legend">
        {MOODS.map((m) => (
          <span key={m.key} className="flex items-center gap-1 k-quiet !text-[10px]">
            <span className="w-2 h-2 rounded-full" style={{ background: m.color }} /> {m.label}
          </span>
        ))}
      </div>

      {data.weeks.some((w) => w.full_week) && (
        <div className="k-quiet !text-[10px] flex items-center gap-1.5 mt-2" data-testid="mood-trend-full-caption">
          <Star size={10} style={{ color: "hsl(38 92% 48%)", fill: "hsl(38 92% 48%)" }} /> a full week means you tagged your mood every day you showed up
        </div>
      )}
    </div>
  );
}
